import { F0Select, F0SelectItemObject } from '@factorialco/f0-react'
import {
  LegalEntityForSelectFragment,
  LegalEntitySelectorF0Document,
  LegalEntitySelectorF0Query,
} from 'generated/resources/graphql'

import { useGraphqlDataSource } from 'modules/core/hooks/useGraphqlDataSource'
import i18n from 'modules/core/lib/i18n'

type LegalEntityOption = LegalEntityForSelectFragment

export interface LegalEntitySelectorF0Props {
  value?: LegalEntityOption | null
  onChange: (legalEntity: LegalEntityOption | null) => void
  label?: string
  placeholder?: string
  error?: string
  required?: boolean
  showCurrency?: boolean
}

const getLegalEntityLabel = (legalEntity: LegalEntityOption, showCurrency?: boolean): string => {
  return showCurrency
    ? `${legalEntity.legalName} - [${legalEntity.currency}]`
    : legalEntity.legalName
}

export const LegalEntitySelectorF0 = ({
  value,
  onChange,
  label = i18n.t('common.legal_entity'),
  placeholder = i18n.t('common.select'),
  error,
  required,
  showCurrency,
}: LegalEntitySelectorF0Props) => {
  const source = useGraphqlDataSource(
    {
      document: LegalEntitySelectorF0Document,
      paginationType: 'infinite-scroll',
      perPage: 20,
      mapVariables: ({ search }) => ({
        search: search || undefined,
      }),
      mapData: (data: LegalEntitySelectorF0Query) =>
        data.companies?.legalEntitiesConnection?.edges?.map((edge) => edge.node) ?? [],
      mapPagination: (data: LegalEntitySelectorF0Query) => ({
        total: data.companies?.legalEntitiesConnection?.totalCount ?? 0,
        cursor: data.companies?.legalEntitiesConnection?.pageInfo?.endCursor ?? undefined,
        hasNextPage: data.companies?.legalEntitiesConnection?.pageInfo?.hasNextPage ?? false,
      }),
    },
    []
  )

  const mapOptions = (
    legalEntity: LegalEntityOption
  ): F0SelectItemObject<string, LegalEntityOption> => ({
    value: String(legalEntity.id),
    label: getLegalEntityLabel(legalEntity, showCurrency),
    item: legalEntity,
  })

  return (
    <F0Select<string, LegalEntityOption>
      label={label}
      placeholder={placeholder}
      source={source}
      mapOptions={mapOptions}
      value={value ? String(value.id) : undefined}
      onChange={(selectedValue: string, originalItem?: LegalEntityOption) => {
        onChange(originalItem ?? null)
      }}
      error={error}
      required={required}
      clearable
    />
  )
}
